import { genericMemo } from "@redotech/react-util/component";
import {
  ArrayPredicateExpression,
  ArrayPredicateOperator,
  predicateOperators,
} from "@redotech/redo-flows-core-schema-common/expressions/array-expression";
import {
  GenericDataType,
  genericPrefixes,
} from "@redotech/redo-flows-core-schema-common/type-system/data-types/generic-types";
import { Schema } from "@redotech/redo-flows-core-schema-common/type-system/schema";
import { useTranslation } from "@redotech/redo-i18n";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { camelToTitleCase } from "@redotech/string/transform";
import { DeepPartial, DeepPartialExcept } from "@redotech/util/type";
import { useEffect, useMemo } from "react";
import { FieldInspector } from ".";

export const ArrayInspector = genericMemo(function ArrayInspector({
  schema,
  dataType,
  selectedField,
  expression,
  setExpression,
  showValidationErrors,
}: {
  schema: Schema;
  dataType: GenericDataType;
  selectedField: Schema["fields"][string];
  expression: DeepPartialExcept<ArrayPredicateExpression, "field">;
  setExpression: (expression: DeepPartial<ArrayPredicateExpression>) => void;
  showValidationErrors?: boolean;
}) {
  const { t } = useTranslation();
  const unwrappedDataType = dataType.slice(
    genericPrefixes.Array.length,
  ) as GenericDataType;

  useEffect(() => {
    if (!expression.type || !expression.operator) {
      setExpression({
        ...expression,
        type: "array_predicate",
        operator: expression.operator ?? "some",
      });
    }
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expression]);

  // The sub-condition matches each element, so it names the array itself.
  const subExpresion = useMemo(
    () => ({ ...expression.condition, field: expression.field }),
    [expression.condition, expression.field],
  );

  const options: RedoListItem<ArrayPredicateOperator>[] = useMemo(() => {
    return predicateOperators.map((operator) => ({
      id: operator,
      type: "text",
      text: camelToTitleCase(operator),
      value: operator,
    }));
  }, []);

  const selectedItem = options.find((o) => o.id === expression.operator);

  return (
    <>
      <RedoSingleSelectDropdownInput<ArrayPredicateOperator>
        label={t("Comparison type")}
        options={options}
        optionSelected={({ item }) => {
          setExpression({ ...expression, operator: item.value });
        }}
        selectedItem={selectedItem}
        size="xs"
      />

      <FieldInspector
        dataType={unwrappedDataType}
        expression={subExpresion}
        schema={schema}
        selectedField={selectedField}
        setExpression={(condition) =>
          setExpression({ ...expression, condition })
        }
        showValidationErrors={showValidationErrors}
      />
    </>
  );
});
